import { useCallback, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import { getCommunityLogoUrlAndType } from "@/react/helpers/communityHelpers";
import { classNames } from "@/react/helpers/twMergeWithCN";
import imagePlaceholder from "@circle-react-shared/BuilderUI/DefaultBlocks/Content/ImgBlock/img_preview.png";
import {
  useAttachmentMetadata,
  useAttachments,
} from "@circle-react-shared/BuilderUI/Media/AttachmentContext";
import { ImageEditorModalWithCrop } from "@circle-react-shared/ImageEditorModalWithCrop";
import { Button } from "@circle-react-shared/uikit/Button";
import { IconButton } from "@circle-react-shared/uikit/HeaderV3/IconButton";
import { useBuilderStore } from "../../../Hooks/useBuilderStore";
import { ImagePickerImage } from "./ImagePickerImage";

interface BuilderUIImagePickerProps {
  className?: string;
  customAspectRatioHelpText?: string;
  hideEmbed?: boolean;
  hideUnsplash?: boolean;
  imgClassNames?: string;
  imgWrapperClassName?: string;
  name: string;
  onDelete?: () => void;
  shouldAddUrlToValue?: boolean;
  shouldAllowRemovingImage?: boolean;
  title?: string;
}

export interface AttachmentValue {
  placeholder?: string;
  signed_id: string | null;
  url?: string;
}

export const ImagePicker = ({
  name,
  imgClassNames,
  hideUnsplash,
  hideEmbed,
  shouldAddUrlToValue,
  shouldAllowRemovingImage = false,
  customAspectRatioHelpText,
  className,
  onDelete,
  imgWrapperClassName,
  title,
}: BuilderUIImagePickerProps) => {
  const { setValue, getValues } = useFormContext();
  const [hasFile, setHasFile] = useState(false);
  const [shouldShowModal, setShouldShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const selectedBlock = useBuilderStore(state => state.getSelectedBlock());
  const isLogoBlock = selectedBlock?.meta?.name === "Logo";

  const { currentCommunity } = usePunditUserContext();
  const { url: communityLogoUrl } = getCommunityLogoUrlAndType(
    currentCommunity,
    false,
  );
  const attachment = getValues(name) as AttachmentValue;
  const signedId = attachment?.signed_id ?? "";
  const defaultPlaceholder = attachment?.placeholder ?? "";

  const attachmentMetadata = useAttachmentMetadata(signedId);
  const { addLocalAttachment } = useAttachments();

  const handleOnImageUpload = useCallback(
    (imageUrl: string, signedId: string) => {
      if (shouldAddUrlToValue) {
        setValue(name, {
          signed_id: signedId,
          url: imageUrl,
        });
      } else {
        setValue(name, {
          signed_id: signedId,
        });
      }
      addLocalAttachment({
        signed_id: signedId,
        url: imageUrl,
      });
    },
    [name, setValue, addLocalAttachment, shouldAddUrlToValue],
  );
  const imageUrl = useMemo(() => {
    if (isLogoBlock) {
      return (
        attachmentMetadata?.url ||
        communityLogoUrl ||
        defaultPlaceholder ||
        imagePlaceholder
      );
    }
    return attachmentMetadata?.url || defaultPlaceholder || imagePlaceholder;
  }, [isLogoBlock, attachmentMetadata, communityLogoUrl, defaultPlaceholder]);

  const handleRemoveImage = useCallback(() => {
    setValue(name, {
      signed_id: null,
    });
    onDelete?.();
  }, [name, setValue, onDelete]);

  const handleCloseModal = useCallback(() => {
    setHasFile(false);
    setShouldShowModal(false);
  }, []);

  return (
    <div
      className={classNames(
        "border-primary divide-primary flex w-full flex-col items-center justify-between divide-y overflow-hidden rounded-lg border",
        className,
      )}
    >
      <button
        type="button"
        className="flex size-full"
        onClick={() => setShouldShowModal(true)}
        disabled={isLoading}
        aria-label={t("builder_ui.prop_type.image_picker.replace")}
      >
        <ImagePickerImage
          url={imageUrl}
          isLoading={isLoading}
          imgClassNames={imgClassNames}
          imgWrapperClassName={imgWrapperClassName}
        />
      </button>

      <div className="flex h-10 w-full items-center justify-between px-3 py-2">
        <Button
          type="button"
          onClick={() => setShouldShowModal(true)}
          variant="text"
          small
          disabled={isLoading}
        >
          {t("builder_ui.prop_type.image_picker.replace")}
        </Button>
        <div>
          {(Boolean(attachment?.signed_id) || Boolean(defaultPlaceholder)) &&
            shouldAllowRemovingImage && (
              <IconButton
                name="20-trash-bin"
                iconSize={20}
                buttonSize={28}
                className="text-dark"
                onClick={handleRemoveImage}
                disabled={isLoading}
                title={t("builder_ui.prop_type.image_picker.delete")}
                ariaLabel={t("builder_ui.prop_type.image_picker.delete")}
              />
            )}
        </div>
      </div>

      <ImageEditorModalWithCrop
        setIsLoading={setIsLoading}
        showModal={shouldShowModal}
        closeModal={handleCloseModal}
        hasFile={hasFile}
        setHasFile={setHasFile}
        hideUnsplash={hideUnsplash}
        title={title}
        hideEmbedUrl={hideEmbed}
        customAspectRatioHelpText={customAspectRatioHelpText}
        // eslint-disable-next-line no-console -- uploadprogress for debugging purposes, will be removed in the future.
        uploadProgress={console.log}
        attachImage={handleOnImageUpload}
      />
    </div>
  );
};
